/**
 * CRM Customer Notes API client.
 *
 * Nested under a customer:
 *   /api/v1/jobs/customers/{customerId}/notes/...
 *
 * Used by `CustomerNotesSection.vue` from both `CustomerEditDialog`
 * (edit existing customer) and `NewCustomerDialog` (initial note saved
 * right after the customer is created).
 */

import axios from '@/api/client'

const BASE = (customerId) => `/jobs/customers/${customerId}/notes`

export const customerNotesApi = {
  /** GET list, sorted by `created_at DESC` server-side. */
  async list(customerId) {
    const { data } = await axios.get(`${BASE(customerId)}/`)
    return Array.isArray(data) ? data : (data?.results || [])
  },

  /**
   * Create a note. If `files` is non-empty we send `multipart/form-data`,
   * otherwise plain JSON. Backend accepts both.
   */
  async create(customerId, { content, files = [] }) {
    if (files && files.length) {
      const fd = new FormData()
      fd.append('content', content)
      files.forEach((f) => fd.append('files', f))
      const { data } = await axios.post(`${BASE(customerId)}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    }
    const { data } = await axios.post(`${BASE(customerId)}/`, { content })
    return data
  },

  /** Edit content (only own/admin allowed by backend). */
  async update(customerId, noteId, { content }) {
    const { data } = await axios.patch(
      `${BASE(customerId)}/${noteId}/`,
      { content },
    )
    return data
  },

  /** Delete a note (cascades attachments + on-disk files). */
  async remove(customerId, noteId) {
    await axios.delete(`${BASE(customerId)}/${noteId}/`)
  },

  /** Upload one or many files to an existing note. */
  async uploadAttachments(customerId, noteId, files) {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f))
    const { data } = await axios.post(
      `${BASE(customerId)}/${noteId}/attachments/`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return data
  },

  /** Remove one attachment from a note. */
  async removeAttachment(customerId, noteId, attId) {
    await axios.delete(`${BASE(customerId)}/${noteId}/attachments/${attId}/`)
  },

  /**
   * Build a URL that downloads the file as an attachment.
   * Component renders this as a link `:href`.
   */
  downloadUrl(customerId, noteId, attId) {
    return `/api/v1${BASE(customerId)}/${noteId}/attachments/${attId}/`
  },
}

export default customerNotesApi
